'use client';
import { useCallback, useState, useEffect } from 'react';
import { isBrowser } from '@s7/platform';
import { useWindowEvent } from '../useWindowEvent/useWindowEvent.js';

function serializeJSON(value, hookName) {
    try {
        return JSON.stringify(value);
    } catch (_) {
        throw new Error(`@s7/hooks ${hookName}: Не удалось выполнить сериализацию значения`);
    }
}
function deserializeJSON(value) {
    try {
        return JSON.parse(value);
    } catch (e) {
        return value;
    }
}
function createStorage(type, hookName) {
    const eventName = type === 'localStorage' ? 'ds-local-storage' : 'ds-session-storage';
    return function useStorage({ key, defaultValue = undefined, getInitialValueInEffect = true, deserialize = deserializeJSON, serialize = (value)=>serializeJSON(value, hookName) }) {
        const readStorageValue = useCallback((skipStorage)=>{
            if (!isBrowser || !(type in window) || skipStorage) {
                return defaultValue;
            }
            const storageValue = window[type].getItem(key);
            return storageValue !== null ? deserialize(storageValue) : defaultValue;
        }, [
            key,
            defaultValue
        ]);
        const [value, setValue] = useState(readStorageValue(getInitialValueInEffect));
        const setStorageValue = useCallback((value)=>{
            if (value instanceof Function) {
                setValue((current)=>{
                    const result = value(current);
                    window[type].setItem(key, serialize(result));
                    window.dispatchEvent(new CustomEvent(eventName, {
                        detail: {
                            key,
                            value: value(current)
                        }
                    }));
                    return result;
                });
            } else {
                window[type].setItem(key, serialize(value));
                window.dispatchEvent(new CustomEvent(eventName, {
                    detail: {
                        key,
                        value
                    }
                }));
                setValue(value);
            }
        }, [
            key
        ]);
        const removeStorageValue = useCallback(()=>{
            window[type].removeItem(key);
            window.dispatchEvent(new CustomEvent(eventName, {
                detail: {
                    key,
                    value: defaultValue
                }
            }));
        }, []);
        useWindowEvent('storage', (event)=>{
            if (event.storageArea === window[type] && event.key === key) {
                var _event_newValue;
                setValue(deserialize((_event_newValue = event.newValue) != null ? _event_newValue : undefined));
            }
        });
        useWindowEvent(eventName, (event)=>{
            if (event.detail.key === key) {
                setValue(event.detail.value);
            }
        });
        useEffect(()=>{
            if (defaultValue !== undefined && value === undefined) {
                setStorageValue(defaultValue);
            }
        }, [
            defaultValue,
            value,
            setStorageValue
        ]);
        useEffect(()=>{
            if (getInitialValueInEffect) {
                setStorageValue(readStorageValue());
            }
        }, [
            key
        ]);
        return [
            value === undefined ? defaultValue : value,
            setStorageValue,
            removeStorageValue
        ];
    };
}

export { createStorage };
//# sourceMappingURL=createStorage.js.map
